import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { DevSessionService } from '../services/dev/dev-session.service';
import { isDevPortalPath } from '../utils/dev-portal-request.util';
import { HttpStatus } from '../utils/http-status.util';

/**
 * Attaches the dev-area RS256 JWT as `Authorization: Bearer <jwt>` to
 * every request that targets a dev endpoint, and recovers the UX when
 * the server rejects the session (401) or rejects the whole claim
 * (404 from the DevOnly filter).
 *
 * Allow-list:
 *   - /api/admin/dev/**
 *   - /api/admin/logs/**
 *   - /api/auth/is-authenticated   (when a dev JWT is currently stored)
 *
 * Everything else passes through untouched so the regular cookie-based
 * user flow is unaffected.
 *
 * On **401 only** (not 404) from an allow-listed URL the interceptor:
 *   1. Flips `DevSessionService.markExpired()` so `DevLayoutComponent`
 *      surfaces the "Session Expired" modal.
 *   2. Re-throws the original error so `finalize` / downstream
 *      `catchError` in the caller still run.
 *
 * Why 401-only: on the wire the backend's `DevOnly` filter returns
 * 404 with `errors: ["Not found"]` for a valid-but-non-dev Bearer
 * (deliberately indistinguishable from a genuine 404 so the dev
 * area's existence is not leaked). Treating every 404 as session
 * loss would mean any unimplemented / misconfigured / temporarily-
 * broken dev endpoint also wipes the session — which is what was
 * happening: on navigation straight after login, one failing dev
 * endpoint nuked a perfectly valid JWT. 401 remains the unambiguous
 * "your Bearer is rejected at the auth layer" signal and is the
 * only safe session-kill trigger.
 *
 * Trade-off accepted: a valid JWT that is somehow missing the `isDev`
 * claim (backend misconfig, key-pair mismatch) now surfaces as 404
 * per-request rather than a session-expired modal. The user stays
 * "logged in" from the FE's perspective and sees empty / error data
 * on the page. They can explicitly sign out and re-login.
 *
 * Navigation to the dev-login page is deliberately NOT done here —
 * moving routes would unmount `DevLayoutComponent` before its modal
 * could render. The modal's OK handler performs the navigation.
 */
export const devJwtInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const devSession = inject(DevSessionService);

  let url: URL;
  try {
    url = new URL(req.url, location.origin);
  } catch {
    return next(req);
  }

  const pathname = url.pathname;
  const isDevPath = isDevPortalPath(pathname);
  const isWhoamiPath = pathname === '/api/auth/is-authenticated';

  const jwt = devSession.devJwt();
  const shouldAttach = !!jwt && (isDevPath || isWhoamiPath);

  const outgoing = shouldAttach
    ? req.clone({ setHeaders: { Authorization: `Bearer ${jwt}` } })
    : req;

  return next(outgoing).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only on 401 from a Bearer-attached request. See the JSDoc above
      // for the 404 rationale.
      if (shouldAttach && error.status === HttpStatus.Unauthorized) {
        if (!environment.production) {
          console.warn(
            '[Dev Session] Invalidating session — 401 on',
            req.method,
            pathname
          );
        }
        devSession.markExpired();
      }
      return throwError(() => error);
    })
  );
};
